/**
 * Drone T+60s predicted-position DOM markers — mockup's `_drawPredMarker`.
 *
 * Layout: the diamond SVG (with optional pulse ring) is the only in-flow
 * child. The T+60s text label is `position:absolute` above it, so the
 * marker's bounding box stays fixed and `anchor:'center'` keeps the
 * diamond glued to the predicted lat/lng under zoom.
 *
 * Updates happen in place via innerHTML / setLngLat — no marker churn.
 */
import mapboxgl from 'mapbox-gl';
import type { Map as MapboxMap } from 'mapbox-gl';
import { droneFilterKey, visibleFleet } from '@/lib/map/droneFilter';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { mapAnimation } from '@/lib/map/instance';
import { isInsideOi, oiByAircraft } from '@/lib/oi/containment';
import { queryClient } from '@/lib/query';

interface PredMeta {
  droneId: string;
  lat: number;
  lng: number;
  flightMode: string | null;
}

function modeLabel(flightMode: string | null): string {
  // Mockup `_drawPredMarker` L8458 derives mode from predAlgoMode; we map our
  // canonical flight_mode (AUTO / WAYPOINT / MISSION / MANUAL) into the same
  // two display buckets.
  if (!flightMode) return 'Autopilot / Auto';
  const m = flightMode.toUpperCase();
  if (m === 'WAYPOINT' || m === 'MISSION') return 'Waypoint Mission';
  return 'Autopilot / Auto';
}

function buildInnerHtml(insideOi: boolean, meta: PredMeta): string {
  const col = insideOi ? '#22a050' : '#c41a1a';
  const fill = insideOi ? '#dcfce7' : '#fee2e2';
  const sz = insideOi ? 22 : 28;
  const labelText = insideOi ? 'T+60s' : 'T+60s ⚠ Outside OI';
  const ringHtml = insideOi
    ? ''
    : `<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);` +
      `width:${sz + 14}px;height:${sz + 14}px;border-radius:50%;` +
      `border:2px solid #c41a1a;opacity:0.5;animation:fly-pred-breach-pulse 1.8s ease-out infinite;pointer-events:none"></div>`;
  // Hover tooltip per mockup `_drawPredMarker` L8454-8462 (sticky bindTooltip).
  // Shows drone id, predicted lat/lng (4 dp), derived flight mode, OI status.
  const oiStatusHtml = insideOi
    ? `<b style="color:#22a050">Inside</b>`
    : `<b style="color:#c41a1a">⚠ Outside — OI Breach Predicted</b>`;
  const tooltipHtml =
    `<div data-role="pred-tooltip" style="display:none;` +
    `position:absolute;left:50%;bottom:100%;transform:translateX(-50%);` +
    `margin-bottom:22px;background:#131a11;color:#f0f4f0;border-radius:8px;` +
    `padding:8px 11px;font-family:IBM Plex Mono,monospace;font-size:0.769em;` +
    `line-height:1.65;white-space:nowrap;z-index:600;` +
    `box-shadow:0 4px 16px rgba(0,0,0,.25);pointer-events:none">` +
      `<div style="font-family:Inter,sans-serif;font-weight:700;color:#fff;` +
      `margin-bottom:3px">${meta.droneId} — Predicted Position</div>` +
      `<div style="color:rgba(255,255,255,.85)">T+60s: ${meta.lat.toFixed(4)}, ${meta.lng.toFixed(4)}</div>` +
      `<div style="color:rgba(255,255,255,.85)">Mode: ${modeLabel(meta.flightMode)}</div>` +
      `<div style="color:rgba(255,255,255,.85)">OI Volume: ${oiStatusHtml}</div>` +
      `<div style="position:absolute;top:100%;left:50%;transform:translateX(-50%);` +
      `border:5px solid transparent;border-top-color:#131a11"></div>` +
    `</div>`;
  return (
    tooltipHtml +
    // Label — absolute, above the diamond
    `<div style="position:absolute;left:50%;bottom:100%;transform:translateX(-50%);margin-bottom:4px;` +
    `font-size:0.538em;font-weight:700;font-family:IBM Plex Mono,monospace;` +
    `color:${col};background:${fill};border:1px solid ${col};` +
    `padding:1px 5px;border-radius:3px;white-space:nowrap;pointer-events:none">${labelText}</div>` +
    // Diamond + (optional) pulse ring — only in-flow content. pointer-events
    // enabled here so the marker el receives the hover that toggles the
    // tooltip above (label + tooltip themselves stay pointer-events:none).
    `<div data-role="pred-diamond" style="position:relative;display:flex;align-items:center;` +
    `justify-content:center;width:${sz + 14}px;height:${sz + 14}px;pointer-events:auto;cursor:pointer">` +
    ringHtml +
    `<svg width="${sz}" height="${sz}" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" style="position:relative;z-index:1;display:block">` +
    `<polygon points="11,1 21,11 11,21 1,11" fill="${fill}" stroke="${col}" stroke-width="${insideOi ? 2 : 2.5}"/>` +
    `<polygon points="11,5 17,11 11,17 5,11" fill="${col}" opacity="${insideOi ? '0.6' : '0.85'}"/>` +
    `</svg>` +
    `</div>`
  );
}

interface ActivePred {
  marker: mapboxgl.Marker;
  el: HTMLElement;
  inside: boolean;
}

export function subscribePredMarkers(map: MapboxMap): () => void {
  const active: Record<string, ActivePred> = {};
  let rafId: number | null = null;

  // Defensive orphan sweep — strips any `[data-pred-did]` DOM markers that
  // the current subscriber doesn't own. Guards against two real edge cases:
  //   • HMR: a previous subscriber's cleanup may not have run because the
  //     module was hot-swapped without React unmount/remount.
  //   • Basemap swap: mapbox `setStyle` keeps the map container's DOM
  //     markers, so if `attachCore` re-entered between cleanup + create the
  //     old diamonds would linger.
  // Without this, the user sees 2-3 stacked diamonds per drone.
  const sweepOrphans = () => {
    const container = map.getContainer();
    container.querySelectorAll<HTMLElement>('[data-pred-did]').forEach((el) => {
      const id = el.dataset.predDid;
      if (id && active[id]?.el === el) return; // owned by this subscriber
      el.closest('.mapboxgl-marker')?.remove();
    });
  };
  // Strip any leftovers from a previous subscriber before we add our own.
  sweepOrphans();

  const sync = () => {
    const ui = useUiStore.getState();
    // Drone projection markers: drones-ctx (showDroneProjection) OR oi-ctx (showOIDronePred).
    const visible =
      (ui.activeContext === 'drones' && ui.showDroneProjection) ||
      (ui.activeContext === 'oi' && ui.showOIDronePred);
    const drones = visible ? visibleFleet() : [];
    const show = visible;
    const seen = new Set<string>();
    // Per-tick OI lookup so the diamond's inside/outside answer matches the
    // polygon colour computed in lib/map/layers/oi.ts (both go through the
    // same isInsideOi helper).
    const oiMap = oiByAircraft();

    if (show) {
      for (const d of drones) {
        const p = d.telemetry.pred1min;
        if (!p) continue;
        const id = d.telemetry.drone_id;
        seen.add(id);
        // Prefer the drone's live OI polygon (from the MQTT flightplan/<UAS_ID>
        // envelope) — this is the source that exists in live mode and the same
        // one lib/map/layers/oi.ts colours the polygon from, so diamond + fill
        // always agree. Fall back to the REST `['oi','list']` cache (mock mode
        // only). Last resort: the telemetry `inside` flag, so a missing polygon
        // never falsely flags a breach.
        const oiPolygon = d.oi?.polygon ?? oiMap.get(id)?.polygon ?? null;
        const insideOi = oiPolygon
          ? isInsideOi(p.lng, p.lat, oiPolygon)
          : p.inside !== false;
        const cur = active[id];

        const meta: PredMeta = {
          droneId: id,
          lat: p.lat,
          lng: p.lng,
          flightMode: d.telemetry.flight_mode ?? null,
        };

        if (!cur) {
          const el = document.createElement('div');
          // No inline `position:relative` — Mapbox's class applies absolute.
          // Outer wrap stays pointer-events:none; the inner [data-role="pred-
          // diamond"] re-enables pointer-events so hover fires for the icon.
          el.style.cssText = 'pointer-events:none';
          el.dataset.predDid = id;
          el.innerHTML = buildInnerHtml(insideOi, meta);
          // Mockup `bindTooltip` (L8454) — sticky, top direction. We toggle
          // an inline tooltip element on mouseenter/leave instead.
          el.addEventListener('mouseenter', () => {
            const tt = el.querySelector('[data-role="pred-tooltip"]') as HTMLElement | null;
            if (tt) tt.style.display = 'block';
          });
          el.addEventListener('mouseleave', () => {
            const tt = el.querySelector('[data-role="pred-tooltip"]') as HTMLElement | null;
            if (tt) tt.style.display = 'none';
          });
          const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
            .setLngLat([p.lng, p.lat])
            .addTo(map);
          active[id] = { marker, el, inside: insideOi };
          continue;
        }

        if (!mapAnimation.value) cur.marker.setLngLat([p.lng, p.lat]);
        // Inside-OI flip flips ring/colour/label; the tooltip body's lat-lng /
        // mode strings drift each tick too, so re-render whenever the diamond
        // is hovered (cheap: only on actual change). Comparing the rendered
        // HTML would be wasteful — gate on the tooltip's visibility instead.
        const tt = cur.el.querySelector('[data-role="pred-tooltip"]') as HTMLElement | null;
        const isOpen = tt?.style.display === 'block';
        if (cur.inside !== insideOi) {
          cur.el.innerHTML = buildInnerHtml(insideOi, meta);
          cur.inside = insideOi;
          if (isOpen) {
            const reopened = cur.el.querySelector('[data-role="pred-tooltip"]') as HTMLElement | null;
            if (reopened) reopened.style.display = 'block';
          }
        } else if (isOpen) {
          // Live-update the visible tooltip's lat/lng row so the user sees
          // motion while hovering. Cheap targeted edit, no innerHTML thrash.
          const rows = tt!.querySelectorAll('div');
          if (rows[1]) rows[1].textContent = `T+60s: ${meta.lat.toFixed(4)}, ${meta.lng.toFixed(4)}`;
          if (rows[2]) rows[2].textContent = `Mode: ${modeLabel(meta.flightMode)}`;
        }
      }
    }

    for (const id of Object.keys(active)) {
      if (!seen.has(id)) {
        active[id]!.marker.remove();
        delete active[id];
      }
    }
    // Belt-and-braces: catch any DOM diamond we don't own (e.g. a previous
    // subscriber's leftovers that materialised mid-sync via setStyle).
    sweepOrphans();
  };

  sync();
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => { rafId = null; sync(); });
  };
  const unsubDrones = useDronesStore.subscribe((s) => s.byId, schedule);
  const unsubShow = useUiStore.subscribe(
    (s) => `${s.showDroneProjection}-${s.showOIDronePred}`,
    sync,
  );
  const unsubCtx = useUiStore.subscribe(droneFilterKey, sync);
  const unsubAnim = mapAnimation.subscribe((v) => { if (!v) sync(); });
  // Re-evaluate containment as soon as the OI list arrives (otherwise the
  // very first frames after page-load fall back to the telemetry `inside`
  // flag — see the fallback branch in `sync`).
  const unsubOiCache = queryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] === 'oi') schedule();
  });

  return () => {
    unsubDrones();
    unsubShow();
    unsubCtx();
    unsubAnim();
    unsubOiCache();
    for (const id of Object.keys(active)) {
      active[id]!.marker.remove();
      delete active[id];
    }
  };
}
